import { ScaleuiWrapper } from "@scaleaq/sui-react/wrappers/ScaleuiWrapper";
import { createContext, useCallback, useEffect, useMemo, useRef, useState } from "react";
import i18next from "src/datahandling/localization/i18n";
import {
	DEFAULT_LIVE_CONFIGURATIONS,
	DEFAULT_LIVE_PROFILE,
	DEFAULT_USER_SETTINGS,
} from "src/models/constants";
import type { LanguageType, ThemeMode } from "src/models/types/common.types";
import type {
	ApplicationFeaturesType,
	CompanyType,
	InsightsLiveUserSettingsType,
	LiveConfigurationsType,
	LiveProfileType,
	UserSettings,
} from "src/models/types/userSettings.types";
import { useUserSettingsConfig } from "../hooks";

type ProfileWithId = LiveProfileType & { profileId?: string };

type CustomerSettingsContextProps = {
	features: ApplicationFeaturesType;
	setFeatures: (newFeatures: ApplicationFeaturesType) => void;
	language: string;
	setLanguage: (newLanguage: string) => void;
	theme: ThemeMode;
	setTheme: (newTheme: string) => void;
	selectedCompany: CompanyType | undefined;
	setSelectedCompany: (newCompany: CompanyType) => void;
	decimalCount: number;
	setDecimalCount: (newDecimalCount: number) => void;
	customerSettings: InsightsLiveUserSettingsType;
	setCustomerSettings: (newUserSettings: InsightsLiveUserSettingsType) => void;
	getUserSettingsLiveView: (viewId: string) => LiveProfileType;
	setUserSettingsLiveView: (view: LiveProfileType) => void;
	setUserSettingsLiveProfile: (newProfile: LiveProfileType) => void;
	getUserSettingsLiveProfile: (profileId: string) => LiveProfileType;
	isLoading: boolean;
};

export const CustomerSettingsContext = createContext<CustomerSettingsContextProps>({
	features: {},
	setFeatures: () => {},
	language: "en",
	setLanguage: () => {},
	theme: "light",
	setTheme: () => {},
	decimalCount: 3,
	setDecimalCount: () => {},
	selectedCompany: undefined,
	setSelectedCompany: () => {},
	customerSettings: DEFAULT_USER_SETTINGS.applicationSpecific.insightsLive,
	setCustomerSettings: () => {},
	setUserSettingsLiveProfile: () => {},
	getUserSettingsLiveProfile: () => DEFAULT_LIVE_PROFILE,
	getUserSettingsLiveView: () => DEFAULT_LIVE_PROFILE,
	setUserSettingsLiveView: () => {},
	isLoading: true,
});

export const CustomerSettingsWrapper = (props: { children: React.ReactNode }) => {
	// *****************************************************
	// Properties
	// *****************************************************

	const { userSettings, setUserSettings, isLoading } = useUserSettingsConfig();

	// *****************************************************
	// Lifecycle
	// *****************************************************

	// Initialize from userSettings or defaults
	const initialCustomerSettings = useMemo(
		() =>
			userSettings?.applicationSpecific?.insightsLive ||
			DEFAULT_USER_SETTINGS.applicationSpecific.insightsLive,
		[userSettings?.applicationSpecific?.insightsLive],
	);

	const [theme, _setTheme] = useState<ThemeMode>(() => {
		const defaultTheme = DEFAULT_USER_SETTINGS.applicationSpecific.insightsLive.theme;
		return (initialCustomerSettings.theme ?? defaultTheme) as ThemeMode;
	});
	const [features, _setFeatures] = useState<ApplicationFeaturesType>(() => {
		return initialCustomerSettings.features ?? {};
	});
	const [language, _setLanguage] = useState<string>(() => {
		const defaultLanguage = DEFAULT_USER_SETTINGS.applicationSpecific.insightsLive.language;
		return (initialCustomerSettings.language ?? defaultLanguage) as string;
	});
	const [selectedCompany, _setSelectedCompany] = useState<CompanyType>(() => {
		const defaultSelectedCompany = DEFAULT_USER_SETTINGS.applicationSpecific.insightsLive.selectedCompany;
		return (initialCustomerSettings.selectedCompany ?? defaultSelectedCompany) as CompanyType;
	});
	const [decimalCount, _setDecimalCount] = useState<number>(() => {
		const defaultDecimalCount = DEFAULT_USER_SETTINGS.applicationSpecific.insightsLive.decimalCount;
		return (initialCustomerSettings.decimalCount ?? defaultDecimalCount) as number;
	});

	const [customerSettings, _setCustomerSettings] =
		useState<InsightsLiveUserSettingsType>(initialCustomerSettings);

	// Refs to avoid stale closures in callbacks
	const customerSettingsRef = useRef(customerSettings);
	const userSettingsRef = useRef(userSettings);

	// Keep refs in sync
	useEffect(() => {
		customerSettingsRef.current = customerSettings;
	}, [customerSettings]);

	useEffect(() => {
		userSettingsRef.current = userSettings;
	}, [userSettings]);

	// Sync customerSettings, theme, and language when userSettings changes
	useEffect(() => {
		if (userSettings?.applicationSpecific?.insightsLive) {
			const newCustomerSettings = userSettings.applicationSpecific.insightsLive;

			_setCustomerSettings(newCustomerSettings);

			if (newCustomerSettings.theme) {
				_setTheme(newCustomerSettings.theme);
			}
			if (newCustomerSettings.language) {
				_setLanguage(newCustomerSettings.language);
			}
			if (newCustomerSettings.features) {
				_setFeatures(newCustomerSettings.features);
			}
			// Use !== undefined to allow 0 as a valid value
			if (newCustomerSettings.decimalCount !== undefined) {
				_setDecimalCount(newCustomerSettings.decimalCount);
			}
			if(newCustomerSettings.selectedCompany) {
				_setSelectedCompany(newCustomerSettings.selectedCompany);
			}
		}
	}, [userSettings?.applicationSpecific?.insightsLive]);

	// Sync i18next language when language state changes
	useEffect(() => {
		if (language && i18next.language !== language) {
			i18next.changeLanguage(language);
		}
	}, [language]);

	// *****************************************************
	// Handling
	// *****************************************************

	const setFeatures = useCallback((newFeatures: ApplicationFeaturesType) => {
		_setFeatures(newFeatures);
	}, []);

	const setLanguage = useCallback(
		async (newLanguage: string) => {
			const currentUserSettings = userSettingsRef.current;
			const currentCustomerSettings = customerSettingsRef.current;

			if (!currentUserSettings) return;

			_setLanguage(newLanguage);

			// Update customerSettings with new language
			const updatedCustomerSettings: InsightsLiveUserSettingsType = {
				...currentCustomerSettings,
				language: newLanguage as LanguageType,
			};
			_setCustomerSettings(updatedCustomerSettings);

			// Update userSettings in backend
			const updatedUserSettings: UserSettings = {
				...currentUserSettings,
				applicationSpecific: {
					...(currentUserSettings.applicationSpecific || {}),
					insightsLive: updatedCustomerSettings,
				},
				// Also update common settings for consistency
				common: {
					...(currentUserSettings.common || {}),
					language: newLanguage,
				},
			};

			await setUserSettings(updatedUserSettings);
		},
		[setUserSettings],
	);

	const setTheme = useCallback(
		async (newTheme: ThemeMode) => {
			const currentUserSettings = userSettingsRef.current;
			const currentCustomerSettings = customerSettingsRef.current;

			if (!currentUserSettings) return;

			_setTheme(newTheme);

			// Update customerSettings with new theme
			const updatedCustomerSettings: InsightsLiveUserSettingsType = {
				...currentCustomerSettings,
				theme: newTheme,
			};
			_setCustomerSettings(updatedCustomerSettings);

			// Update userSettings in backend
			const updatedUserSettings: UserSettings = {
				...currentUserSettings,
				applicationSpecific: {
					...(currentUserSettings.applicationSpecific || {}),
					insightsLive: updatedCustomerSettings,
				},
				// Also update common settings for consistency
				common: {
					...(currentUserSettings.common || {}),
					uiTheme: newTheme,
				},
			};

			await setUserSettings(updatedUserSettings);
		},
		[setUserSettings],
	);

	const setSelectedCompany = useCallback(
		async (newCompany: CompanyType) => {
			const currentUserSettings = userSettingsRef.current;
			const currentCustomerSettings = customerSettingsRef.current;

			if (!currentUserSettings) return;

			_setSelectedCompany(newCompany);

			// Update customerSettings with new selectedCompany
			const updatedCustomerSettings: InsightsLiveUserSettingsType = {
				...currentCustomerSettings,
				selectedCompany: newCompany,
			};
			_setCustomerSettings(updatedCustomerSettings);

			// Update userSettings in backend
			const updatedUserSettings: UserSettings = {
				...currentUserSettings,
				applicationSpecific: {
					...(currentUserSettings.applicationSpecific || {}),
					insightsLive: updatedCustomerSettings,
				},
			};

			await setUserSettings(updatedUserSettings);
		},
		[setUserSettings],
	);

	const setCustomerSettings = useCallback((newConfigurations: InsightsLiveUserSettingsType) => {
		_setCustomerSettings(newConfigurations);
	}, []);

	const setDecimalCount = useCallback(
		async (newDecimalCount: number) => {
			const currentUserSettings = userSettingsRef.current;
			const currentCustomerSettings = customerSettingsRef.current;

			if (!currentUserSettings) return;

			_setDecimalCount(newDecimalCount);

			// Update customerSettings with new decimalCount
			const updatedCustomerSettings: InsightsLiveUserSettingsType = {
				...currentCustomerSettings,
				decimalCount: newDecimalCount,
			};
			_setCustomerSettings(updatedCustomerSettings);

			// Update userSettings in backend
			const updatedUserSettings: UserSettings = {
				...currentUserSettings,
				applicationSpecific: {
					...(currentUserSettings.applicationSpecific || {}),
					insightsLive: updatedCustomerSettings,
				},
			};

			await setUserSettings(updatedUserSettings);
		},
		[setUserSettings],
	);

	// *****************************************************
	// Handling :: User Settings
	// *****************************************************

	const getUserSettingsLiveView = useCallback(
		(viewId: string): LiveProfileType => {
			// Get current customerSettings
			const currentCustomerSettings =
				customerSettings || DEFAULT_USER_SETTINGS.applicationSpecific.insightsLive;

			// Get liveConfigurations from customerSettings
			const currentLiveConfigurations =
				currentCustomerSettings.liveConfigurations || DEFAULT_LIVE_CONFIGURATIONS;

			const views = currentLiveConfigurations.views || [];
			const view = views.find((v) => v.id === viewId || (v as ProfileWithId).profileId === viewId);

			// Return the found view, or fall back to DEFAULT_LIVE_PROFILE if not found
			return view || DEFAULT_LIVE_PROFILE;
		},
		[customerSettings],
	);

	const setUserSettingsLiveView = useCallback(
		async (newView: LiveProfileType) => {
			const currentUserSettings = userSettingsRef.current;
			const currentCustomerSettings =
				customerSettingsRef.current || DEFAULT_USER_SETTINGS.applicationSpecific.insightsLive;

			if (!currentUserSettings) return;

			const viewId = newView.id || (newView as ProfileWithId).profileId;
			if (!viewId) {
				console.error("View must have an id or profileId");
				return;
			}

			// Get current liveConfigurations from customerSettings
			const currentLiveConfigurations =
				currentCustomerSettings.liveConfigurations || DEFAULT_LIVE_CONFIGURATIONS;

			// Get existing views or initialize empty array
			const existingViews = currentLiveConfigurations.views || [];

			// Check if view exists by id
			const existingViewIndex = existingViews.findIndex(
				(view) => view.id === viewId || (view as ProfileWithId).profileId === viewId,
			);

			// Create updated views array
			const updatedViews: LiveProfileType[] =
				existingViewIndex >= 0
					? // Update existing view
						existingViews.map((view, index) =>
							index === existingViewIndex ? { ...view, ...newView, id: viewId } : view,
						)
					: // Add new view
						[...existingViews, { ...newView, id: viewId }];

			// Create updated liveConfigurations
			const updatedLiveConfigurations: LiveConfigurationsType = {
				...currentLiveConfigurations,
				views: updatedViews,
			};

			// Create updated customerSettings with the new liveConfigurations
			const updatedCustomerSettings: InsightsLiveUserSettingsType = {
				...currentCustomerSettings,
				liveConfigurations: updatedLiveConfigurations,
			};

			// Update local state
			_setCustomerSettings(updatedCustomerSettings);

			// Update userSettings in backend
			const updatedUserSettings: UserSettings = {
				...currentUserSettings,
				applicationSpecific: {
					...(currentUserSettings.applicationSpecific || {}),
					insightsLive: updatedCustomerSettings,
				},
			};

			await setUserSettings(updatedUserSettings);
		},
		[setUserSettings],
	);

	const setUserSettingsLiveProfile = useCallback(
		async (newProfile: LiveProfileType) => {
			const currentUserSettings = userSettingsRef.current;
			const currentCustomerSettings =
				customerSettingsRef.current || DEFAULT_USER_SETTINGS.applicationSpecific.insightsLive;

			if (!currentUserSettings) return;

			const profileId = newProfile.id || (newProfile as ProfileWithId).profileId;
			if (!profileId) {
				console.error("Profile must have an id or profileId");
				return;
			}

			// Get current liveConfigurations from customerSettings
			const currentLiveConfigurations =
				currentCustomerSettings.liveConfigurations || DEFAULT_LIVE_CONFIGURATIONS;

			// Get existing profiles or initialize empty array
			const existingProfiles = currentLiveConfigurations.profiles || [];

			// Check if profile exists by id
			const existingProfileIndex = existingProfiles.findIndex(
				(profile) => profile.id === profileId || (profile as ProfileWithId).profileId === profileId,
			);

			// Create updated profiles array
			const updatedProfiles: LiveProfileType[] =
				existingProfileIndex >= 0
					? // Update existing profile
						existingProfiles.map((profile, index) =>
							index === existingProfileIndex ? { ...profile, ...newProfile, id: profileId } : profile,
						)
					: // Add new profile
						[...existingProfiles, { ...newProfile, id: profileId }];

			// Also update the corresponding view in the views array
			const existingViews = currentLiveConfigurations.views || [];
			const existingViewIndex = existingViews.findIndex(
				(view) => view.id === profileId || (view as ProfileWithId).profileId === profileId,
			);

			// Create updated views array - sync the view with the profile data
			const updatedViews: LiveProfileType[] =
				existingViewIndex >= 0
					? // Update existing view with the new profile data
						existingViews.map((view, index) =>
							index === existingViewIndex ? { ...view, ...newProfile, id: profileId } : view,
						)
					: // Add new view with the profile data
						[...existingViews, { ...newProfile, id: profileId }];

			// Create updated liveConfigurations with both profiles and views
			const updatedLiveConfigurations: LiveConfigurationsType = {
				...currentLiveConfigurations,
				profiles: updatedProfiles,
				views: updatedViews,
			};

			// Create updated customerSettings with the new liveConfigurations
			const updatedCustomerSettings: InsightsLiveUserSettingsType = {
				...currentCustomerSettings,
				liveConfigurations: updatedLiveConfigurations,
			};

			// Update local state
			_setCustomerSettings(updatedCustomerSettings);

			// Update userSettings in backend
			const updatedUserSettings: UserSettings = {
				...currentUserSettings,
				applicationSpecific: {
					...(currentUserSettings.applicationSpecific || {}),
					insightsLive: updatedCustomerSettings,
				},
			};

			await setUserSettings(updatedUserSettings);
		},
		[setUserSettings],
	);

	const getUserSettingsLiveProfile = useCallback(
		(profileId: string): LiveProfileType => {
			// Get current customerSettings
			const currentCustomerSettings =
				customerSettings || DEFAULT_USER_SETTINGS.applicationSpecific.insightsLive;

			// Get liveConfigurations from customerSettings
			const currentLiveConfigurations =
				currentCustomerSettings.liveConfigurations || DEFAULT_LIVE_CONFIGURATIONS;

			const profiles = currentLiveConfigurations.profiles || [];
			const profile = profiles.find(
				(p) => p.id === profileId || (p as ProfileWithId).profileId === profileId,
			);

			// Return the found profile, or fall back to DEFAULT_LIVE_PROFILE if not found
			return profile || DEFAULT_LIVE_PROFILE;
		},
		[customerSettings],
	);

	// *****************************************************
	// Context
	// *****************************************************

	const context = useMemo(() => {
		return {
			features,
			setFeatures,
			language,
			setLanguage,
			theme,
			setTheme,
			selectedCompany,
			setSelectedCompany,
			customerSettings,
			setCustomerSettings,
			decimalCount,
			setDecimalCount,
			setUserSettingsLiveProfile,
			getUserSettingsLiveProfile,
			getUserSettingsLiveView,
			setUserSettingsLiveView,
			isLoading,
		};
	}, [
		features,
		language,
		theme,
		selectedCompany,
		customerSettings,
		decimalCount,
		setCustomerSettings,
		setTheme,
		setLanguage,
		setSelectedCompany,
		setFeatures,
		setUserSettingsLiveProfile,
		getUserSettingsLiveProfile,
		getUserSettingsLiveView,
		setUserSettingsLiveView,
		setDecimalCount,
		isLoading,
	]);

	// *****************************************************
	// Render
	// *****************************************************

	// Don't render children until settings are loaded
	if (isLoading) {
		return null;
	}

	return (
		<CustomerSettingsContext.Provider value={context as CustomerSettingsContextProps}>
			<ScaleuiWrapper theme={theme} locale={language}>
				{props.children}
			</ScaleuiWrapper>
		</CustomerSettingsContext.Provider>
	);
};
